import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AppLayout from "../layouts/AppLayout";
import AuthLayout from "../layouts/AuthLayout";
import { RequireAuth, RedirectIfAuthed, SubscriptionGate, RequirePermission } from "./ProtectedRoute";

// Safe lazy loader with auto-retry and cache-bust recovery on ChunkLoadError
const lazyRetry = (componentImport) =>
  lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      console.warn("ChunkLoadError detected, retrying import...", error);
      // Try again once after clearing stale module cache
      try {
        return await componentImport();
      } catch (retryError) {
        // If still failing, trigger a hard reload once to get fresh chunks
        const refreshed = sessionStorage.getItem("chunk_reload_retry");
        if (!refreshed) {
          sessionStorage.setItem("chunk_reload_retry", "true");
          window.location.reload();
          return;
        }
        sessionStorage.removeItem("chunk_reload_retry");
        throw retryError;
      }
    }
  });

const LandingPage = lazyRetry(() => import("../components/screens/LandingPage"));
const Login = lazyRetry(() => import("../components/screens/login"));
const Dashboard = lazyRetry(() => import("../components/screens/dashboard"));
const Chat = lazyRetry(() => import("../components/screens/chat"));
const ContactManagement = lazyRetry(() => import("../components/screens/ContactManagement"));
const ContactDetail = lazyRetry(() => import("../components/screens/ContactDetail"));
const FollowUps = lazyRetry(() => import("../components/screens/FollowUps"));
const CampaignForm = lazyRetry(() => import("../components/screens/campignform"));
const CampaignReports = lazyRetry(() => import("../components/screens/CampaignReports"));
const CampaignAnalytics = lazyRetry(() => import("../components/screens/CampaignAnalytics"));
const CampaignScheduled = lazyRetry(() => import("../components/screens/CampaignScheduled"));
const Group = lazyRetry(() => import("../components/screens/group"));
const ImportContact = lazyRetry(() => import("../components/screens/importcontact"));
const TemplateList = lazyRetry(() => import("../components/screens/templatelist"));
const Account = lazyRetry(() => import("../components/screens/account"));
const ChangePassword = lazyRetry(() => import("../components/screens/changePassword"));
const Profile = lazyRetry(() => import("../components/screens/profile"));
const Billing = lazyRetry(() => import("../components/screens/billing"));
const AutoReplies = lazyRetry(() => import("../components/screens/autoReplies"));
const Developer = lazyRetry(() => import("../components/screens/developer"));
const TeamInbox = lazyRetry(() => import("../components/screens/teamInbox"));
const QuickReplies = lazyRetry(() => import("../components/screens/quickReplies"));
const ActivityLog = lazyRetry(() => import("../components/screens/activityLog"));
const ApiKeys = lazyRetry(() => import("../components/screens/ApiKeys"));
const Staff = lazyRetry(() => import("../components/screens/staff"));
const RenewPage = lazyRetry(() => import("../components/screens/RenewPage"));
const PrivacyPolicy = lazyRetry(() => import("../components/screens/PrivacyPolicy"));
const TermsAndConditions = lazyRetry(() => import("../components/screens/TermsAndConditions"));
const Support = lazyRetry(() => import("../components/screens/Support"));
const DeleteAccountGuide = lazyRetry(() => import("../components/screens/DeleteAccountGuide"));

const RouteFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
  </div>
);

const AppRoute = () => {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/delete-account" element={<DeleteAccountGuide />} />
          <Route path="/delete-account-guide" element={<DeleteAccountGuide />} />
          <Route path="/account-deletion" element={<DeleteAccountGuide />} />

          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <AuthLayout>
                  <Login />
                </AuthLayout>
              </RedirectIfAuthed>
            }
          />

          <Route path="/renew" element={<RequireAuth><RenewPage /></RequireAuth>} />

          <Route element={<RequireAuth><SubscriptionGate /></RequireAuth>}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<RequirePermission permissions={["r_campaign", "r_account", "r_message", "r_recipient", "r_template"]}><Dashboard /></RequirePermission>} />
              <Route path="/chat" element={<RequirePermission permissions={["r_message", "s_message"]}><Chat /></RequirePermission>} />
              <Route path="/contact" element={<RequirePermission permissions={["r_recipient", "crud_recipient"]}><ContactManagement /></RequirePermission>} />
              <Route path="/contact/:id" element={<RequirePermission permissions={["r_recipient", "crud_recipient"]}><ContactDetail /></RequirePermission>} />
              <Route path="/followups" element={<RequirePermission permissions={["r_recipient", "crud_recipient"]}><FollowUps /></RequirePermission>} />
              <Route path="/campaigns" element={<RequirePermission permissions={["r_campaign", "crud_campaign"]}><CampaignForm /></RequirePermission>} />
              <Route path="/campaigns/reports" element={<RequirePermission permissions={["r_campaign", "crud_campaign"]}><CampaignReports /></RequirePermission>} />
              <Route path="/campaigns/analytics" element={<RequirePermission permissions={["r_campaign", "crud_campaign"]}><CampaignAnalytics /></RequirePermission>} />
              <Route path="/campaigns/scheduled" element={<RequirePermission permissions={["r_campaign", "crud_campaign"]}><CampaignScheduled /></RequirePermission>} />
              <Route path="/group" element={<RequirePermission permissions={["r_group", "crud_group"]}><Group /></RequirePermission>} />
              <Route path="/importcontact" element={<RequirePermission permissions={["r_recipient", "crud_recipient"]}><ImportContact /></RequirePermission>} />
              <Route path="/template" element={<RequirePermission permissions={["r_template", "crud_template", "reg_template", "sync_template"]}><TemplateList /></RequirePermission>} />
              <Route path="/account" element={<RequirePermission permissions={["r_account", "crud_account"]}><Account /></RequirePermission>} />
              <Route path="/staff" element={<RequirePermission roles={["owner", "admin"]}><Staff /></RequirePermission>} />
              <Route path="/team" element={<RequirePermission roles={["owner", "admin"]}><Staff /></RequirePermission>} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/billing" element={<RequirePermission roles={["owner", "admin"]}><Billing /></RequirePermission>} />
              <Route path="/auto-replies" element={<RequirePermission permissions={["r_message", "s_message"]}><AutoReplies /></RequirePermission>} />
              <Route path="/developer" element={<RequirePermission roles={["owner", "admin"]}><Developer /></RequirePermission>} />
              <Route path="/inbox" element={<RequirePermission permissions={["r_message", "s_message"]}><TeamInbox /></RequirePermission>} />
              <Route path="/quick-replies" element={<RequirePermission permissions={["r_message", "s_message"]}><QuickReplies /></RequirePermission>} />
              <Route path="/api-keys" element={<RequirePermission roles={["owner", "admin"]}><ApiKeys /></RequirePermission>} />
              <Route path="/activity-log" element={<RequirePermission roles={["owner", "admin"]}><ActivityLog /></RequirePermission>} />
              <Route path="/support" element={<Support />} />
              <Route path="/help" element={<Support />} />
              <Route path="/contact-support" element={<Support />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoute;
